import type { ImportKind, ImportPreview, Mapping } from "../../types/api";

export type WizardStep = "upload" | "map" | "validate";

export interface SideState {
  file: File | null;
  fileError: string | null;
  preview: ImportPreview | null;
  mapping: Mapping;
  saveMapping: boolean;
  mappingName: string;
}

export interface WizardState {
  step: WizardStep;
  shipment: SideState;
  invoice: SideState;
  settings: { money: string; weight: string };
}

export type WizardAction =
  | { type: "file"; kind: ImportKind; file: File | null; error: string | null }
  | { type: "previewed"; kind: ImportKind; preview: ImportPreview }
  | { type: "uploadFailed"; kind: ImportKind; message: string }
  | { type: "map"; kind: ImportKind; field: string; column: string | null }
  | { type: "saveMapping"; kind: ImportKind; enabled: boolean; name?: string }
  | { type: "settings"; money?: string; weight?: string }
  | { type: "step"; step: WizardStep }
  | { type: "reset" };

const emptySide: SideState = {
  file: null,
  fileError: null,
  preview: null,
  mapping: {},
  saveMapping: false,
  mappingName: "",
};

export const initialWizardState: WizardState = {
  step: "upload",
  shipment: emptySide,
  invoice: emptySide,
  settings: { money: "5.00", weight: "1" },
};

function defaultMappingName(preview: ImportPreview): string {
  if (preview.saved_mapping) return preview.saved_mapping.name;
  const stem = preview.filename.replace(/\.(xlsx|csv)$/i, "");
  return `${stem} format`.slice(0, 120);
}

export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case "file":
      return { ...state, [action.kind]: { ...emptySide, file: action.file, fileError: action.error } };
    case "previewed":
      return {
        ...state,
        [action.kind]: {
          ...state[action.kind],
          fileError: null,
          preview: action.preview,
          mapping: { ...action.preview.mapping },
          mappingName: defaultMappingName(action.preview),
        },
      };
    case "uploadFailed":
      return { ...state, [action.kind]: { ...state[action.kind], preview: null, fileError: action.message } };
    case "map": {
      const side = state[action.kind];
      return { ...state, [action.kind]: { ...side, mapping: { ...side.mapping, [action.field]: action.column } } };
    }
    case "saveMapping": {
      const side = state[action.kind];
      return {
        ...state,
        [action.kind]: { ...side, saveMapping: action.enabled, mappingName: action.name ?? side.mappingName },
      };
    }
    case "settings":
      return {
        ...state,
        settings: {
          money: action.money ?? state.settings.money,
          weight: action.weight ?? state.settings.weight,
        },
      };
    case "step":
      return { ...state, step: action.step };
    case "reset":
      return initialWizardState;
  }
}

/** Required fields that still have no column. */
export function missingRequired(side: SideState): string[] {
  if (!side.preview) return [];
  return side.preview.fields.filter((field) => field.required && !side.mapping[field.key]).map((field) => field.key);
}

const TOLERANCE = /^\d{1,7}(\.\d{1,2})?$/;

export function toleranceError(value: string, unit: "money" | "weight"): string | null {
  if (!value.trim()) return "Enter a tolerance (0 for an exact match).";
  if (!TOLERANCE.test(value.trim())) {
    return unit === "money" ? "Use dollars and cents, e.g. 5.00." : "Use pounds with up to two decimals, e.g. 1.";
  }
  return null;
}
